/**
 * Minimum native version for the store app.
 *
 * The CRM bundle reaches installed phones over the air, the native binary
 * only through the App Store. Once a bundle needs a newer binary — push,
 * universal links, the keychain all arrived with 1.1.0 — a phone on the old
 * build must update rather than run half an app. The server says which
 * version is the floor (GET /appUpdate/policy, from NATIVE_MIN_VERSION), so
 * the floor is raised only once the new version can actually be downloaded
 * from the App Store; until then there is no floor and nothing is blocked.
 * Fails open: no answer, a bad answer or no network means no block.
 */
import { createLogger, summarizeError } from "../utils/logger";

const log = createLogger("native.updateGate");

const API_BASE = process.env.REACT_APP_BASE_URL || "";
const buildUrl = (path) => {
  if (!API_BASE) return path;
  return API_BASE.endsWith("/") ? `${API_BASE.slice(0, -1)}${path}` : `${API_BASE}${path}`;
};

const POLICY_TIMEOUT_MS = 8000;

/** "1.2.3" -> [1, 2, 3]; null when it is not a version. */
export function parseVersion(value) {
  if (typeof value !== "string") return null;
  const m = value.trim().match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:\.(\d+))?$/);
  if (!m) return null;
  return [m[1], m[2], m[3], m[4]].map((p) => (p === undefined ? 0 : Number(p)));
}

/** -1 when a < b, 0 when equal, 1 when a > b. Unparseable sides compare equal. */
export function compareVersions(a, b) {
  const pa = parseVersion(a);
  const pb = parseVersion(b);
  if (!pa || !pb) return 0;
  for (let i = 0; i < 4; i++) {
    if (pa[i] < pb[i]) return -1;
    if (pa[i] > pb[i]) return 1;
  }
  return 0;
}

/** Block only when both versions are real and the phone is below the floor. */
export function shouldBlock(currentVersion, minimumVersion) {
  if (!parseVersion(currentVersion) || !parseVersion(minimumVersion)) return false;
  return compareVersions(currentVersion, minimumVersion) < 0;
}

/**
 * The server's policy, or null when it has none (or cannot be reached).
 * @returns {Promise<{minimumNativeVersion: string|null, storeUrl: string|null}|null>}
 */
export async function fetchUpdatePolicy() {
  try {
    const res = await fetch(buildUrl("/appUpdate/policy"), {
      signal: AbortSignal.timeout(POLICY_TIMEOUT_MS),
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (!body || typeof body !== "object") return null;
    return {
      minimumNativeVersion:
        typeof body.minimumNativeVersion === "string" ? body.minimumNativeVersion : null,
      storeUrl: typeof body.storeUrl === "string" && body.storeUrl ? body.storeUrl : null,
    };
  } catch (error) {
    log.warn("update_gate.policy_unavailable", { ...summarizeError(error) });
    return null;
  }
}

/** The installed binary's version string, or null off the native shell. */
export async function getNativeVersion() {
  try {
    const { App } = await import("@capacitor/app");
    const info = await App.getInfo();
    return typeof info?.version === "string" ? info.version : null;
  } catch (error) {
    log.warn("update_gate.version_unavailable", { ...summarizeError(error) });
    return null;
  }
}
